import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import type { LeaderboardEntry } from '../lib/api.js';

export function Leaderboard({ myId }: { myId: string }) {
  const [rows, setRows] = useState<LeaderboardEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .leaderboard()
      .then((r) => {
        if (cancelled) return;
        setRows(r);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'не удалось загрузить');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <h2 className="heading-display text-xl text-gold">Топ игроков</h2>
        <span className="text-[10px] uppercase tracking-wider text-ivory/40">по рейтингу</span>
      </div>
      {error && (
        <p
          className="rounded-2xl px-3 py-2 text-xs text-coral"
          style={{
            background: 'var(--color-glass)',
            border: '1px solid color-mix(in oklab, var(--color-coral) 35%, transparent)',
          }}
        >
          {error}
        </p>
      )}
      {!error && rows === null && <p className="text-xs text-ivory/55">Загружаем…</p>}
      {rows && rows.length === 0 && (
        <p className="text-xs text-ivory/55">
          Пока никто не сыграл достаточно партий, чтобы попасть в топ.
        </p>
      )}
      {rows && rows.length > 0 && (
        <ol className="flex flex-col gap-1.5">
          {rows.map((r, i) => {
            const isMe = r.id === myId;
            const place = i + 1;
            return (
              <li
                key={r.id}
                className="flex items-center gap-3 rounded-2xl px-3 py-2"
                style={{
                  background: 'var(--color-glass)',
                  border: isMe
                    ? '1px solid color-mix(in oklab, var(--color-gold) 55%, transparent)'
                    : '1px solid color-mix(in oklab, var(--color-gold) 18%, transparent)',
                }}
              >
                <span
                  className="grid h-6 w-6 place-items-center rounded-full text-xs"
                  style={{
                    background: place <= 3 ? 'var(--color-gold)' : 'transparent',
                    color: place <= 3 ? 'var(--color-felt-deep)' : 'var(--color-ivory)',
                    border:
                      place <= 3
                        ? 'none'
                        : '1px solid color-mix(in oklab, var(--color-ivory) 20%, transparent)',
                    fontFamily: 'var(--font-display)',
                  }}
                >
                  {place}
                </span>
                <div
                  className="grid h-7 w-7 place-items-center overflow-hidden rounded-full"
                  style={{ border: '1px solid var(--color-gold)', background: 'var(--color-felt-deep)' }}
                >
                  {r.photoUrl ? (
                    <img src={r.photoUrl} alt={r.firstName} className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-xs text-gold" style={{ fontFamily: 'var(--font-display)' }}>
                      {r.firstName.slice(0, 1).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm" style={{ fontFamily: 'var(--font-display)', fontSize: 14 }}>
                    {r.firstName}
                    {isMe && <span className="text-ivory/45"> · вы</span>}
                  </span>
                  <span className="truncate text-[11px] text-ivory/55">
                    {r.totalGames} игр · {r.totalGames > 0 ? Math.round((r.totalWins / r.totalGames) * 100) : 0}%
                  </span>
                </div>
                <span style={{ fontFamily: 'var(--font-display)', fontSize: 16, color: 'var(--color-gold)' }}>
                  {r.rating}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
